import { useTranslation } from 'react-i18next';
import { IconButton, Box, Stack } from '@mui/material';

import { theme } from '@/modules/common/theme';
import { DownloadIcon } from '@/assets/icons';
import { useFloorplanImportExport } from '@/modules/floorplanV2/hooks';
import { StyledTooltip } from '@/modules/common/components/StyledTooltip';

import { type BaseNotification as NotificationInterface } from '../helpers/types';
import { Icon } from '@/modules/toolbar/Icon';

const iconButtonStyles = {
  transition: 'background-color 0.3s ease, border-radius 0.3s ease',
  width: '18px',
  height: '18px',

  padding: 0,
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
    borderRadius: '2px',
  },
  '&:focus': {
    outline: 'none',
    backgroundColor: 'transparent',
  },
  '&:active': {
    backgroundColor: '#DFE5FC',
    borderRadius: '2px',
  },
};

type SeverityActionConfig = {
  actions: Array<{
    tooltipKey: string;
    icon: React.ReactElement | null;
    onClick: () => void;
    boxScale?: string;
    sx?: object;
  }>;
};

const SeverityAction: React.FC<{ config: SeverityActionConfig }> = ({ config }) => {
  const { t } = useTranslation(['interface']);
  return (
    <Stack
      direction='row'
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        cursor: 'pointer',
        gap: '8px',
        width: '100%',
      }}
    >
      {config.actions.map((action) => (
        <StyledTooltip key={action.tooltipKey} title={t(action.tooltipKey)} placement='top-end'>
          <IconButton
            size='small'
            disableRipple
            disableFocusRipple
            sx={{
              ...iconButtonStyles,
              ...action.sx,
            }}
            onClick={action.onClick}
          >
            <Box
              sx={{
                transform: action.boxScale || 'scale(1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {action.icon}
            </Box>
          </IconButton>
        </StyledTooltip>
      ))}
    </Stack>
  );
};

const NotificationSeverityActions = ({
  notification,
}: {
  notification?: NotificationInterface;
}) => {
  const { downloadFloorplan } = useFloorplanImportExport();

  const severityConfig: Record<'fatal' | 'error', SeverityActionConfig> = {
    fatal: {
      actions: [
        {
          tooltipKey: 'interface:notifications.downloadFloorplan',
          icon: <DownloadIcon sx={{ color: theme.palette.others.black }} />,
          onClick: () => downloadFloorplan(),
          boxScale: 'scale(0.7)',
        },
        {
          tooltipKey: 'interface:notifications.contactSupport',
          icon: <Icon icon='contact' />,
          onClick: () => window.open('https://t-hive.freshdesk.com/support/tickets/new', '_blank'),
          sx: { mr: '16px' },
        },
      ],
    },
    error: {
      actions: [
        ...(notification && notification.readMoreUrl !== 'null'
          ? [
              {
                tooltipKey: 'interface:notifications.readMore',
                icon: <Icon icon='readMore' />,
                onClick: () =>
                  notification.readMoreUrl && window.open(notification.readMoreUrl, '_blank'),
                sx: { mr: '16px' },
              },
            ]
          : []),
      ],
    },
  };

  if (notification) {
    const normalize = notification.severity.toLowerCase() as keyof typeof severityConfig;
    const config = severityConfig[normalize];
    if (!config || !Array.isArray(config.actions)) {
      return null;
    }
    return <SeverityAction config={config} />;
  }
};

export default NotificationSeverityActions;
